import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AppState } from '@redux/store'
import { ActionModule } from '@typesApp/ActionModule'
import { HostSession, HostEnvironment } from '@typesApp/Environment'
import { Session, CustomerContext, CommerceCustomer, CommerceCustomerContext, Customer } from '@typesApp/Session'
import { apiConfigurationError, getCommerceApiUrl, getRequestHeaders } from '@utils/apiUtil'
import { createGuestSession, createSessionWithToken, toCustomer, toCustomerContext } from '@utils/sessionApiUtil'

export const sessionsApi = createApi({
  reducerPath: 'sessions',
  baseQuery: fetchBaseQuery(),
  keepUnusedDataFor: 300,
  tagTypes: ['Session', 'CustomerContext', 'AuthToken'],

  endpoints: builder => ({
    getSession: builder.query<Session, string>({
      providesTags: [{ type: 'Session' }],
      queryFn: async (sessionId, queryApi, _extraOptions, fetchFn) => {
        const state = queryApi.getState() as AppState
        const hostSession = state.connector.session as HostSession
        const isPartiallyAuthenticated = hostSession?.isPartiallyAuthenticated
        const env = state.connector?.environment as HostEnvironment
        const actionModule = state.connector.actionModule as ActionModule

        if (hostSession?.isGuest) {
          const session = createGuestSession(sessionId, hostSession)
          return { data: session }
        }

        if (typeof actionModule?.getCustomer === 'function') {
          try {
            const customer = await actionModule.getCustomer()
            const session = createSessionWithToken(sessionId, hostSession, customer)
            return { data: session }
          } catch (error) {
            console.error('Error calling get customer API from action module', error)
            return { error: error as FetchBaseQueryError }
          }
        }

        if (!env?.ecommerceTransactionHost) {
          return apiConfigurationError('customer')
        }

        try {
          const headers = getRequestHeaders(isPartiallyAuthenticated, hostSession)
          const url = getCommerceApiUrl(env, 'customer')
          const result = (await fetchFn({
            url,
            headers,
            method: 'GET',
            credentials: 'include',
          })) as { data: CommerceCustomer; error?: unknown }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError }
          }

          const customer = toCustomer(result.data)
          const session = createSessionWithToken(sessionId, hostSession, customer)
          return { data: session }
        } catch (error) {
          console.error('Error calling getSession API', error)
          return { error: error as FetchBaseQueryError }
        }
      },
    }),

    getCustomerContext: builder.query<CustomerContext, void>({
      providesTags: [{ type: 'CustomerContext' }],
      queryFn: async (args, queryApi, _extraOptions, fetchFn) => {
        const state = queryApi.getState() as AppState
        const hostSession = state.connector.session as HostSession
        const isPartiallyAuthenticated = hostSession?.isPartiallyAuthenticated
        const env = state.connector?.environment as HostEnvironment
        const actionModule = state.connector.actionModule as ActionModule

        if (typeof actionModule?.getCustomerContext === 'function') {
          try {
            const context = await actionModule.getCustomerContext()
            return { data: { ...context, sessionId: `${context.sessionId}` } }
          } catch (error) {
            console.error('Error calling customer context API from action module', error)
            return { error: error as FetchBaseQueryError }
          }
        }

        if (!env?.ecommerceTransactionHost) {
          return apiConfigurationError('customerContext')
        }

        try {
          const headers = getRequestHeaders(isPartiallyAuthenticated, hostSession)
          const url = getCommerceApiUrl(env, 'customerContext')
          const result = (await fetchFn({
            url,
            headers,
            method: 'GET',
            credentials: 'include',
          })) as { data: CommerceCustomerContext; error?: unknown }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError }
          }

          const context = toCustomerContext(result.data)
          return { data: context }
        } catch (error) {
          console.error('Error calling customer context API', error)
          return { error: error as FetchBaseQueryError }
        }
      },
    }),
  }),
})

export const {
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useLazyGetCustomerContextQuery,
  usePrefetch: useSessionPrefetch,
} = sessionsApi
